import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useMemo, useRef } from 'react';

import type { AssetId, ChainAddressId } from '@infinex/asset-config';
import { makeChainAddressId } from '@infinex/asset-config';
import { type ChainKey } from '@infinex/chain-config';
import { cn } from '@infinex/ui-styles';

import { AssetListSection } from './AssetListSection';
import type { ChainTokenBalanceListItemProps } from './ChainTokenBalanceListItem';
import { ChainTokenBalanceListItem } from './ChainTokenBalanceListItem';
import type { OnAssetSelection } from './type';

import type { BalancesByChainAddressId } from '../../features/balances';
import type { WalletView } from '../../hooks/portfolio/useWalletView';
import type { FullAsset } from '../../types';

const LIST_ROW_HEIGHT_ESTIMATE_PX = 68;
const LIST_OVERSCAN = 5;
const MAX_VISIBLE_ITEMS_WHEN_UNMEASURED = LIST_OVERSCAN + 10;

export type ChainTokenBalanceListUIProps = {
  /** The title of the section */
  sectionTitle: string;
  /** The token balances to display */
  tokenBalances: BalancesByChainAddressId;
  sortedTokenBalanceKeys: {
    chainAddressId: ChainAddressId;
    isDisabled:
      | {
          disabledLabel?: string;
          disabledDetails?: string;
        }
      | false
      | undefined;
  }[];
  /** If true, applies accent highlight to selected items */
  highlightSelected?: boolean;
  /** Currently selected asset (for highlighting) */
  selectedAsset?: FullAsset;
  buildTestId: (...args: string[]) => string | undefined;
  className?: string;
  listClassName?: string;
  titleSize?: 'base' | 'small';
  walletView?: WalletView;
  /** If true, shows the custody view pill based on current wallet view */
  showCustodyViewPill?: boolean;
  /** Optional element to render inline with the section title */
  titleAdornment?: React.ReactNode;
  /** Optional ref to external scroll container used by virtualizer */
  scrollElementRef?: React.RefObject<HTMLElement | null>;
  /** Gap in px between virtualizer rows. Defaults to 0. */
  rowGap?: number;
  /** Selection mode: single (click to select) or multi (checkbox toggle) */
  selectionMode?: 'single' | 'multi';
  /** Set of selected keys for multi-select mode */
  selectedKeys?: Set<string>;
  /** Callback for multi-select toggle - passes chainAddressId and asset data */
  onToggle?: (
    chainAddressId: ChainAddressId,
    assetData: {
      chain: ChainKey;
      address: string;
      assetType: 'token' | 'nft';
      assetGroupId?: AssetId;
      decimals: number;
    }
  ) => void;

  /** The function to call when a token is selected */
  onSelection: OnAssetSelection;

  hideSectionTitle?: boolean;
  /** If false, the section title scrolls with the list instead of pinning */
  stickyTitle?: boolean;
} & Pick<ChainTokenBalanceListItemProps, 'showWalletSources'>;

export const ChainTokenBalanceListUI = memo(function ChainTokenBalanceListUI({
  sectionTitle,
  tokenBalances,
  highlightSelected = false,
  selectedAsset,
  onSelection,
  sortedTokenBalanceKeys,
  buildTestId,
  listClassName,
  className,
  titleSize = 'base',
  showCustodyViewPill,
  walletView,
  titleAdornment,
  scrollElementRef,
  selectionMode = 'single',
  selectedKeys,
  onToggle,
  hideSectionTitle = false,
  stickyTitle,
  showWalletSources,
  rowGap,
}: ChainTokenBalanceListUIProps) {
  return (
    <AssetListSection
      title={sectionTitle}
      hideSectionTitle={hideSectionTitle}
      stickyTitle={stickyTitle}
      className={listClassName}
      showCustodyViewPill={showCustodyViewPill}
      walletView={walletView}
      titleSize={titleSize}
      titleAdornment={titleAdornment}
    >
      <VirtualizedList
        buildTestId={buildTestId}
        tokenBalances={tokenBalances}
        sortedTokenBalanceKeys={sortedTokenBalanceKeys}
        className={className}
        showWalletSources={showWalletSources}
        scrollElementRef={scrollElementRef}
        selectionMode={selectionMode}
        highlightSelected={highlightSelected}
        selectedKeys={selectedKeys}
        selectedAsset={selectedAsset}
        onSelection={onSelection}
        onToggle={onToggle}
        rowGap={rowGap}
      />
    </AssetListSection>
  );
});

const VirtualizedList = memo(function VirtualizedList(
  props: Pick<
    ChainTokenBalanceListUIProps,
    | 'tokenBalances'
    | 'sortedTokenBalanceKeys'
    | 'className'
    | 'showWalletSources'
    | 'scrollElementRef'
    | 'onSelection'
    | 'onToggle'
    | 'selectionMode'
    | 'highlightSelected'
    | 'selectedKeys'
    | 'selectedAsset'
    | 'buildTestId'
    | 'rowGap'
  >
) {
  const {
    tokenBalances,
    sortedTokenBalanceKeys,
    className,
    showWalletSources,
    scrollElementRef,
    onSelection,
    onToggle,
    selectionMode,
    highlightSelected,
    selectedKeys,
    selectedAsset,
    buildTestId,
    rowGap = 0,
  } = props;

  const tokens = useMemo(
    () =>
      sortedTokenBalanceKeys.filter(
        ({ chainAddressId }) => !!tokenBalances[chainAddressId]
      ),
    [sortedTokenBalanceKeys, tokenBalances]
  );

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: tokens.length,
    getScrollElement: () => scrollElementRef?.current ?? parentRef.current,
    estimateSize: () => LIST_ROW_HEIGHT_ESTIMATE_PX,
    overscan: LIST_OVERSCAN,
    gap: rowGap,
  });

  // During dialog route animations, ResizeObserver may briefly measure full
  // content height instead of constrained viewport. Detect and cap items.
  const rawVirtualItems = rowVirtualizer.getVirtualItems();
  const measuredHeight = rowVirtualizer.scrollRect?.height ?? 0;
  const totalSize = rowVirtualizer.getTotalSize();
  const isUnmeasured = measuredHeight === 0 || measuredHeight >= totalSize;
  const virtualItems =
    isUnmeasured && rawVirtualItems.length > MAX_VISIBLE_ITEMS_WHEN_UNMEASURED
      ? rawVirtualItems.slice(0, MAX_VISIBLE_ITEMS_WHEN_UNMEASURED)
      : rawVirtualItems;

  return (
    <div ref={parentRef} className={cn('relative w-full')}>
      <div
        className="relative w-full"
        style={{ height: rowVirtualizer.getTotalSize() }}
      >
        {virtualItems.map((virtualRow) => {
          const item = tokens[virtualRow.index];
          if (!item) return null;

          const { chainAddressId, isDisabled } = item;
          const chainBalance = tokenBalances[chainAddressId]!;

          return (
            <div
              key={chainAddressId}
              // Measure real row heights — with the fixed 68px estimate,
              // taller rows overflow their slot and paint over the row gap
              ref={rowVirtualizer.measureElement}
              data-index={virtualRow.index}
              className="absolute top-0 left-0 w-full"
              style={{
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <ChainTokenBalanceListItem
                className={className}
                chainAssetBalance={chainBalance}
                showWalletSources={showWalletSources}
                onSelection={onSelection}
                onToggle={() =>
                  onToggle?.(chainAddressId, {
                    chain: chainBalance.chain,
                    address: chainBalance.assetAddress,
                    assetType: chainBalance.type,
                    assetGroupId: chainBalance.assetId,
                    decimals: chainBalance.decimals,
                  })
                }
                selectionMode={selectionMode}
                highlightSelected={highlightSelected}
                isSelected={checkIsSelected({
                  selectionMode,
                  selectedKeys,
                  selectedAsset,
                  chainAddressId,
                })}
                isDisabled={!!isDisabled}
                disabledLabel={
                  (isDisabled && isDisabled.disabledLabel) || undefined
                }
                disabledDetails={
                  (isDisabled && isDisabled.disabledDetails) || undefined
                }
                buildTestId={buildTestId}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});

function checkIsSelected({
  selectionMode,
  selectedKeys,
  selectedAsset,
  chainAddressId,
}: {
  selectionMode: ChainTokenBalanceListUIProps['selectionMode'];
  selectedKeys: ChainTokenBalanceListUIProps['selectedKeys'];
  selectedAsset: ChainTokenBalanceListUIProps['selectedAsset'];
  chainAddressId: ChainAddressId;
}) {
  if (selectionMode === 'multi') {
    return selectedKeys?.has(chainAddressId);
  }

  return (
    selectedAsset &&
    makeChainAddressId(selectedAsset.chain, selectedAsset.address) ===
      chainAddressId
  );
}
